// src/components/NotificationToasts.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PiBell } from "react-icons/pi";
import { IoMdClose } from "react-icons/io";
import { useNotifications } from "./Notifications"; // ajusta esta ruta si es necesario


// Normaliza el payload que viene del Provider.
// El Provider envía: { id, code, title, message, createdAt, raw }
function normalize(msg) {
  const id =
    msg.id ||
    msg.uuid ||
    msg.code ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Mostramos title y message (title en negritas, message debajo).
  const title =
    msg.title ||
    msg.text ||
    msg.body?.title ||
    msg.code || // fallback a código si no hay título
    "Notification";

  const message =
    msg.message ||
    msg.body?.message ||
    msg.text ||
    ""; // último fallback

  return {
    id,
    title,
    message,
    ts: msg.ts || Date.now(),
    raw: msg,
  };
}

export default function NotificationToasts({
  position = "bottom-right",
  duration = 6000, // ms visible antes de auto-cerrar
  max = 4,         // máximo de toasts simultáneos
}) {
  const { onNotification } = useNotifications();
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map()); // para limpiar timeouts al desmontar
  /*  
  useEffect(() => {
    // Notificación de prueba
    const testNotification = {
      id: "test-toast",
      code: "USER-41003031",
      title: "User Approved", // lo que enviaría el provider traducido
      message: "Your account has been successfully approved.",
      createdAt: new Date().toISOString(),
    };

    const n = normalize(testNotification);
    setToasts([n]);

    const timeout = setTimeout(() => {
      setToasts([]);
    }, duration);

    return () => clearTimeout(timeout);
  }, []);
*/

  // Contenedor flotante según posición
  const containerClass = useMemo(() => {
    const base =
      "pointer-events-none fixed z-[99999] p-4 flex flex-col gap-3";
    const map = {
      "top-right": `${base} top-4 right-4 items-end`,
      "top-left": `${base} top-4 left-4 items-start`,
      "bottom-right": `${base} bottom-4 right-4 items-end`,
      "bottom-left": `${base} bottom-4 left-4 items-start`,
      "top-center": `${base} top-4 left-1/2 -translate-x-1/2 items-center`,
      "bottom-center": `${base} bottom-4 left-1/2 -translate-x-1/2 items-center`,
    };
    return map[position] || map["bottom-right"];
  }, [position]);



  // Suscripción a notificaciones del Provider
  useEffect(() => {
    const off = onNotification((msg) => {
      const n = normalize(msg);
      // Si no hay nada que mostrar, salir
      if (!n.title && !n.message) return;

      // Insertar al inicio y limitar a "max"
      setToasts((prev) => {
        const next = [n, ...prev].slice(0, max);
        return next;
      });

      // Auto-dismiss + guardar id del timeout
      const tid = setTimeout(() => {
        setToasts((curr) => curr.filter((t) => t.id !== n.id));
        timersRef.current.delete(n.id);
      }, duration);

      timersRef.current.set(n.id, tid);
    });

    return off;
  }, [onNotification, duration, max]);

  // Limpieza total al desmontar
  useEffect(() => {
    return () => {
      timersRef.current.forEach((tid) => clearTimeout(tid));
      timersRef.current.clear();
    };
  }, []);

  const dismiss = (id) => {
    // limpiar timeout si existe
    const tid = timersRef.current.get(id);
    if (tid) {
      clearTimeout(tid);
      timersRef.current.delete(id);
    }
    // remover toast
    setToasts((curr) => curr.filter((t) => t.id !== id));
  };

  return (
    <div className={containerClass} aria-live="polite" aria-atomic="true">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
            role="status"
            className="pointer-events-auto relative overflow-hidden w-[270px] md:w-[320px] max-w-[92vw]
                     border rounded-2xl shadow-xl backdrop-blur-md
                     bg-[#070F24]/35 dark:bg-white/20 border-white/10 text-white"
          >
            {/* Botón cerrar ARRIBA A LA DERECHA */}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="absolute top-3 right-3 text-white/70 hover:scale-105 transition cursor-pointer"
              aria-label="Dismiss notification"
              title="Dismiss"
            >
              <IoMdClose className="w-4 h-4" />
            </button>

            {/* Contenido */}
            <div className="p-4">{/* pt para no chocar con la X; pr para no tapar el texto */}
              <div className="flex items-center gap-3">
                <PiBell className="shrink-0 w-7 h-7 opacity-90" aria-hidden="true" />
                <div className="flex-1">
                  <p className="font-semibold text-white leading-snug">{t.title}</p>
                  {t.message ? (
                    <p className="mt-1 leading-normal text-white/90">{t.message}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Barra de progreso ABSOLUTA (hereda el clip del card) */}
            <div className="absolute inset-x-0 bottom-0 h-[3px] bg-white/10">
              <div
                className="h-full"
                style={{
                  backgroundColor: "#086EF9",
                  width: "100%",
                  animation: `toastbar ${duration}ms linear forwards`,
                }}
              />
            </div>

            <style>{`
            @keyframes toastbar {
              from { width: 100%; }
              to { width: 0%; }
            }
          `}</style>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

}